import { useState } from 'react';
import {
  Paper,
  Title,
  Stack,
  Timeline,
  Text,
  Badge,
  Group,
  Select,
  TextInput,
  Loader,
  Center,
  Box,
} from '@mantine/core';
import { IconSearch, IconFilter } from '@tabler/icons-react';
import { format } from 'date-fns';
import { useAssetAuditLogs } from '@/hooks/api/useAuditLogs';
import type { AssetAuditLog } from '@/types/models';

interface AssetChangeHistoryProps {
  organizationId: string;
  assetId: string;
}

const getActionColor = (actionType: string) => {
  switch (actionType) {
    case 'CREATE':
      return 'green';
    case 'DELETE':
      return 'red';
    case 'UPDATE':
      return 'blue';
    case 'ASSIGN':
    case 'UNASSIGN':
      return 'violet';
    case 'STATUS_CHANGE':
      return 'orange';
    case 'LOCATION_CHANGE':
      return 'teal';
    case 'AUDIT':
      return 'cyan';
    case 'MAINTENANCE':
      return 'yellow';
    case 'DOCUMENT_UPLOAD':
    case 'DOCUMENT_DELETE':
      return 'grape';
    default:
      return 'gray';
  }
};

const getActionLabel = (actionType: string) => {
  return actionType.replace('_', ' ');
};

export function AssetChangeHistory({ organizationId, assetId }: AssetChangeHistoryProps) {
  const [actionFilter, setActionFilter] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: logs = [], isLoading } = useAssetAuditLogs(organizationId, assetId);

  // Filter logs
  const filteredLogs = logs.filter((log) => {
    if (actionFilter && log.action_type !== actionFilter) {
      return false;
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        log.change_summary?.toLowerCase().includes(searchLower) ||
        log.field_changed?.toLowerCase().includes(searchLower) ||
        log.old_value?.toLowerCase().includes(searchLower) ||
        log.new_value?.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  // Get unique action types for filter
  const actionTypes = Array.from(new Set(logs.map((log) => log.action_type)));

  if (isLoading) {
    return (
      <Paper p="md" withBorder>
        <Center h={200}>
          <Loader />
        </Center>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <div>
          <Title order={3}>Change History</Title>
          <Text size="sm" c="dimmed">
            Complete audit trail of all changes to this asset
          </Text>
        </div>

        {/* Filters */}
        <Group>
          <TextInput
            placeholder="Search changes..."
            leftSection={<IconSearch size={16} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Filter by action"
            leftSection={<IconFilter size={16} />}
            data={[
              { value: '', label: 'All Actions' },
              ...actionTypes.map((type) => ({
                value: type,
                label: getActionLabel(type),
              })),
            ]}
            value={actionFilter}
            onChange={setActionFilter}
            clearable
            style={{ width: 200 }}
          />
        </Group>

        {/* Timeline */}
        {filteredLogs.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No changes found
          </Text>
        ) : (
          <Timeline bulletSize={24} lineWidth={2}>
            {filteredLogs.map((log) => (
              <Timeline.Item
                key={log.id}
                bullet={
                  <Box
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: `var(--mantine-color-${getActionColor(log.action_type)}-6)`,
                    }}
                  />
                }
              >
                <Stack gap="xs">
                  <Group gap="xs">
                    <Badge size="sm" color={getActionColor(log.action_type)}>
                      {getActionLabel(log.action_type)}
                    </Badge>
                    {log.is_major_event && (
                      <Badge size="sm" variant="light" color="red">
                        Major
                      </Badge>
                    )}
                    <Text size="xs" c="dimmed">
                      {format(new Date(log.created_at), 'MMM dd, yyyy h:mm a')}
                    </Text>
                  </Group>

                  <Text size="sm" fw={500}>
                    {log.change_summary}
                  </Text>

                  {log.field_changed && (
                    <Box>
                      <Text size="xs" c="dimmed" fw={500}>
                        Field: {log.field_changed}
                      </Text>
                      {log.old_value && log.new_value && (
                        <Group gap="xs" mt={4}>
                          <Badge size="xs" variant="light" color="red">
                            From: {log.old_value}
                          </Badge>
                          <Text size="xs" c="dimmed">
                            →
                          </Text>
                          <Badge size="xs" variant="light" color="green">
                            To: {log.new_value}
                          </Badge>
                        </Group>
                      )}
                    </Box>
                  )}

                  {log.user_name && (
                    <Text size="xs" c="dimmed">
                      By: {log.user_name}
                    </Text>
                  )}

                  {log.metadata && Object.keys(log.metadata).length > 0 && (
                    <Box
                      p="xs"
                      style={{
                        backgroundColor: 'var(--mantine-color-gray-0)',
                        borderRadius: 4,
                      }}
                    >
                      <Text size="xs" fw={500} c="dimmed" mb={4}>
                        Additional Details:
                      </Text>
                      {Object.entries(log.metadata).map(([key, value]) => (
                        <Text key={key} size="xs" c="dimmed">
                          {key}: {String(value)}
                        </Text>
                      ))}
                    </Box>
                  )}
                </Stack>
              </Timeline.Item>
            ))}
          </Timeline>
        )}
      </Stack>
    </Paper>
  );
}
